import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { type ReactNode, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Form } from '@app/components/core/form/Form'
import { useInitiativeFormWatchAutosave } from '@app/pages/dashboard/initiative/shared/useInitiativeFormWatchAutosave'
import { useTranslations } from '@i18n/useLanguage'
import { enrich } from '@i18n/localizeHelper'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import {
  createContactDetailsSchema,
  type ContactDetailsFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import {
  contactDetailsDefaultValues,
  contactDetailsToEditPayload,
} from '@app/pages/dashboard/initiative/shared/forms/contactDetails/contactDetailsFormMappers'
import {
  ContactDetailsFormFields,
  ContactDetailsFormInitialSync,
} from '@app/pages/dashboard/initiative/shared/forms/contactDetails/ContactDetailsFormFields'
import { ContactDetailsUserPrefillFromProfile } from '@app/pages/dashboard/initiative/shared/forms/contactDetails/ContactDetailsUserPrefill'
import Column from '@app/components/layout/Column'

const CURRENT_STEP = initiativeCreationSteps.contactDetails

export const ContactDetailsStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const formValidation = useTranslations().app.form.validation

  const {
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhoneNumber,
  } = initiative

  const schema = useMemo(
    () =>
      createContactDetailsSchema({
        required: formValidation.required,
        invalidEmail: formValidation.invalidEmail,
        phoneMinLength: enrich(formValidation.minLength, 9),
      }),
    [formValidation],
  )

  const initialValues = useMemo(
    () =>
      contactDetailsDefaultValues({
        contactFirstName,
        contactLastName,
        contactEmail,
        contactPhoneNumber,
      }),
    [
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhoneNumber,
    ],
  )

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Column gap="2.5rem" alignItems="stretch">
        <Form<ContactDetailsFormValues>
          schema={schema}
          defaultValues={initialValues}
          mode="onChange"
          onSubmit={() => undefined}
        >
          <>
            <ContactDetailsFormInitialSync initial={initialValues} />
            <ContactDetailsUserPrefillFromProfile />
            <ContactDetailsOnboardingEffects
              initiativeId={initiative.id}
              completeStep={completeStep}
              uncompleteStep={uncompleteStep}
            />
            <ContactDetailsFormFields />
          </>
        </Form>
      </Column>
    </CreationGuide>
  )
}

const ContactDetailsOnboardingEffects = ({
  initiativeId,
  completeStep,
  uncompleteStep,
}: {
  initiativeId: number
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}): ReactNode => {
  const { formState, control } = useFormContext<ContactDetailsFormValues>()
  useInitiativeFormWatchAutosave({
    initiativeId,
    control,
    toPayload: (v) => ({
      ...contactDetailsToEditPayload(v),
      onboardingStep: CURRENT_STEP,
    }),
    watchDeps: (v) =>
      [
        v.contactFirstName,
        v.contactLastName,
        v.contactEmail,
        v.contactPhoneNumber,
      ] as const,
  })
  useEffect(() => {
    if (formState.isValid) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [formState.isValid, completeStep, uncompleteStep])
  return null
}
